import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MessageCircle, XCircle, Clock, CheckCircle, NotebookText } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { motion } from 'motion/react';
import { PageShell } from './layout/PageShell';

interface MyGigInterestsPageProps {
  accessToken: string;
  isVisible?: boolean;
  onBack: () => void;
  onViewGig: (gigId: string) => void;
  onExploreGigs?: () => void;
  /** When true (desktop modal), hide the sub-page header; modal chrome shows back button. */
  hideHeader?: boolean;
}

export function MyGigInterestsPage({
  accessToken,
  isVisible = true,
  onBack,
  onViewGig,
  onExploreGigs,
  hideHeader = false,
}: MyGigInterestsPageProps) {
  const [interests, setInterests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  // iOS PWA: JS-based sticky sub-page header (position:sticky broken in standalone)
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  // iOS PWA: when page becomes visible, scroll .sub-page-content to top (persistent mount keeps scroll position)
  useEffect(() => {
    if (!isVisible) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const el = subPageContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [isVisible]);

  useEffect(() => {
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const fetchMyInterests = async (options?: { silent?: boolean }) => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/my-interests`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (!silent) {
          toast.error(data.error || 'Kunne ikke hente interesser');
        }
        return;
      }

      const data = await response.json();
      // Defensive UI filter: hide non-active gigs even if stale data is returned.
      setInterests((data.interests || []).filter((interest: any) => interest?.gig?.status === 'active'));
    } catch (error) {
      if (!silent) {
        toast.error('Kunne ikke hente interesser');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && isVisible) {
      void fetchMyInterests();
    } else {
      setLoading(false);
    }
  }, [accessToken, isVisible]);

  useEffect(() => {
    const handleGigInterestsRefresh = () => {
      if (!accessToken || !isVisible) return;
      void fetchMyInterests({ silent: true });
    };

    window.addEventListener('talentfunn:gig-interests-refresh', handleGigInterestsRefresh);
    return () => {
      window.removeEventListener('talentfunn:gig-interests-refresh', handleGigInterestsRefresh);
    };
  }, [accessToken, isVisible]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (!isVisible || !accessToken) return;
      if (document.visibilityState === 'visible') {
        void fetchMyInterests({ silent: true });
      }
    };

    const handleOnline = () => {
      if (!isVisible || !accessToken) return;
      void fetchMyInterests({ silent: true });
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [accessToken, isVisible]);

  const handleWithdrawInterest = async (gigId: string, interestId: string) => {
    setWithdrawingId(interestId);
    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}/interest`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke trekke tilbake interesse');
        return;
      }

      toast.success('Interesse trukket tilbake');
      setInterests((prev) => prev.filter((i) => i.id !== interestId));
      window.dispatchEvent(new Event('talentfunn:gig-interests-refresh'));
      window.dispatchEvent(new Event('talentfunn:gigs-refresh'));
    } catch (error) {
      toast.error('Kunne ikke trekke tilbake interesse');
    } finally {
      setWithdrawingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'I dag';
    if (diffDays === 1) return 'I går';
    if (diffDays < 7) return `${diffDays} dager siden`;
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'contacted':
        return (
          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Kontaktet
          </span>
        );
      case 'rejected':
        return (
          <span className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Avvist
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Venter svar
          </span>
        );
    }
  };

  const formatBudget = (min: number, max: number) => {
    if (min === max) return `${min.toLocaleString('nb-NO')} kr`;
    return `${min.toLocaleString('nb-NO')} - ${max.toLocaleString('nb-NO')} kr`;
  };

  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
      {!hideHeader && (
        <>
          <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />
          <div ref={stickyHeaderRef} className="sub-page-header bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-150 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-3">
              <NotebookText className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h2 className="font-semibold">Aktuelle oppdrag</h2>
                <p className="text-sm text-gray-600">
                  {interests.length} {interests.length === 1 ? 'oppdrag' : 'oppdrag'} du har vist interesse for
                </p>
              </div>
            </div>
          </div>
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        </>
      )}

      {/* Content */}
      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
          {hideHeader && (
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
              <NotebookText className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-900">Aktuelle oppdrag</h2>
                <p className="text-sm text-gray-600">
                  {interests.length} {interests.length === 1 ? 'oppdrag' : 'oppdrag'} du har vist interesse for
                </p>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-12">
              <Loader size="xl" className="text-purple-600" />
              <p className="mt-4 text-gray-700">Laster interesser...</p>
            </div>
          ) : interests.length === 0 ? (
            <div className="text-center py-12 bg-white p-8 xl:rounded-2xl xl:shadow-md">
              <NotebookText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl mb-3 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Ingen interesser ennå
              </h3>
              <p className="text-gray-600 mb-6">
                Du har ikke vist interesse for noen oppdrag ennå. Gå til oppdrag-fanen for å finne spennende muligheter!
              </p>
              <button
                onClick={onExploreGigs ?? onBack}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold inline-flex items-center gap-2"
              >
                <NotebookText className="w-5 h-5" />
                Utforsk oppdrag
              </button>
            </div>
          ) : (
            <div className="space-y-3 xl:space-y-4">
              {interests.map((interest: any) => (
                <motion.div
                  key={interest.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 xl:shadow-md xl:border-0 hover:shadow-lg transition-shadow"
                >
                  {/* Gig info */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => onViewGig(interest.gig.id)}
                      className="text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors duration-150 text-left mb-1 truncate block w-full"
                      >
                        {interest.gig.title}
                      </button>
                      <p className="text-sm text-gray-600 mb-2">{interest.gig.county}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-700">
                        <span className="font-medium text-purple-700">
                          {interest.gig.budget || 'Budsjett ikke oppgitt'}
                        </span>
                      </div>
                    </div>
                    {getStatusBadge(interest.status)}
                  </div>

                  {/* Gig description */}
                  <p className="text-gray-700 mb-4 line-clamp-2">{interest.gig.description}</p>

                  {/* Skills */}
                  {interest.gig.skills && interest.gig.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {interest.gig.skills.slice(0, 5).map((skill: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-xs rounded-lg border border-purple-200"
                        >
                          {skill}
                        </span>
                      ))}
                      {interest.gig.skills.length > 5 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{interest.gig.skills.length - 5}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Your pitch */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-4 border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Din melding:</span>
                    </div>
                    <p className="text-gray-800 italic">"{interest.message}"</p>
                  </div>

                  {/* Timestamp */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span>Viste interesse {formatDate(interest.createdAt)}</span>
                    {interest.gig.status === 'closed' && (
                      <span className="text-amber-700 font-medium bg-amber-100 px-2 py-0.5 rounded">Oppdraget er avsluttet</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => onViewGig(interest.gig.id)}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold flex items-center justify-center gap-2"
                    >
                      <NotebookText className="w-4 h-4" />
                      Se oppdrag
                    </button>
                    {interest.status === 'pending' && interest.gig.status === 'active' && (
                      <button
                        onClick={() => handleWithdrawInterest(interest.gig.id, interest.id)}
                        disabled={withdrawingId === interest.id}
                        className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-150 active:scale-[0.98] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {withdrawingId === interest.id ? (
                          <>
                            <Loader size="sm" className="text-gray-400" />
                            Trekker tilbake...
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4" />
                            Trekk tilbake
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </PageShell>
      </div>
    </div>
  );
}
